/**
 * Eine Notiz als Karte — bewusst sparsam: Überschrift, Text, Punkte zum Abhaken
 * und die Wege zur Erledigung (Mail schreiben, Anrufen, Termin, Angebot). Der
 * Weg, der zur Aufgabe passt („Angebot schreiben" → Angebot), steht vorn und
 * ist hervorgehoben (`passendeTat`).
 * Mailadressen und Nummern stehen nicht da: der Knopf kennt sie (Benjamin,
 * 22.09.2026). Alles Seltene liegt hinter „⋯".
 *
 * 13.10.2026 (Benjamin): Ein Tipp auf den Kundennamen öffnet die Kundendaten. „Termin"
 * neben „Entfällt" gibt der Aufgabe einen Termin — sie wird bis dahin zurückgestellt;
 * das wird öfter gebraucht als ein Kalendertermin, der deshalb ins Menü wandert. Nur wo
 * die Aufgabe selbst nach einem Kalendertermin klingt („Nachfragen für Termin"), bleibt
 * „Termin" der Weg in den Planer.
 *
 * `kompakt` ist die Fassung für Kunde, Projekt, Termin und Leed: dort steht der
 * Bezug schon auf der Seite.
 */
import { useState, type MouseEvent as ReactMouseEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useIstTelefon } from "../../hooks/useIstTelefon";
import { fehlerText } from "@vimaana/ui";

import {
  useNotizAendern, useNotizLoeschen, usePunktAendern, usePunktAnlegen, usePunktLoeschen,
} from "../../hooks/useNotizen";
import {
  bezugTitel, datumDe, hauptnummer, passendeTat, projektTitelVorschlag, telLink, vorname, type Notiz, type Tat,
} from "../../lib/notizen";
import { useBewertungVerzichten } from "../../hooks/useBewertung";
import PostSchreiben from "../projekt/PostSchreiben";
import BewertungSchreiben from "./BewertungSchreiben";
import { MitBelegVerweisen } from "./BelegVerweise";
import NotizPunkte from "./NotizPunkte";
import NotizMenue from "./NotizMenue";
import PhasenMail from "./PhasenMail";
import { usePhasenaufgabeZuordnen } from "../../hooks/usePhasenaufgaben";
import { ZurueckgestelltHinweis, ZurueckstellenWahl } from "./Zurueckstellen";
import "../../styles/projekte.css";
import "../../styles/notizen.css";
import "../../styles/bewertung.css";

type Props = {
  notiz: Notiz;
  kompakt?: boolean;
  /** Name, der schon als Gruppenüberschrift steht — die Karte wiederholt ihn nicht. */
  gruppe?: string;
  /** Kundendatensatz als Blatt öffnen statt zur Kundenseite zu springen. */
  onKunde?: (kundeId: string) => void;
};

export default function NotizKarte({ notiz, kompakt = false, gruppe = "", onKunde }: Props) {
  // Angebot/Rechnung anlegen nur am Computer (20.10.2026).
  const telefon = useIstTelefon();
  const aendern = useNotizAendern();
  const loeschen = useNotizLoeschen();
  const punktNeu = usePunktAnlegen();
  const punktAendern = usePunktAendern();
  const punktWeg = usePunktLoeschen();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurf, setEntwurf] = useState(notiz.text);
  const [punktOffen, setPunktOffen] = useState(false);
  const [neuerPunkt, setNeuerPunkt] = useState("");
  const [schreiben, setSchreiben] = useState(false);
  const [bewerten, setBewerten] = useState(false);
  const [phasenMail, setPhasenMail] = useState(false);
  const verzichten = useBewertungVerzichten();
  const zuordnen = usePhasenaufgabeZuordnen();
  const [spaeterWahl, setSpaeterWahl] = useState(false);
  // Das × am Punkt: am Rechner beim Überfahren, am Telefon erst über „⋯ → Punkte bearbeiten".
  const [punkteBearbeiten, setPunkteBearbeiten] = useState(false);
  // Antippen des Textes bearbeitet ihn (Benjamin, 20.10.2026) — Text wie Punkte.
  /** Klick auf einen Beleg-Verweis oder Link im Text öffnet den — nicht den Editor. */
  const aufText = (e: ReactMouseEvent) => !(e.target as HTMLElement).closest("a, button");

  const k = notiz.kontext;
  const b = notiz.bezug;
  const fehler = aendern.error || loeschen.error || punktNeu.error || punktAendern.error || punktWeg.error || verzichten.error
    || zuordnen.error;
  // Aufgabe „Bewertung erbitten" (Migration 192): eigener Knopf mit der fertigen Mail.
  const bewertung = notiz.bewertung?.status === "offen" ? notiz.bewertung : null;
  // Phasenaufgabe (Etappe 4): die Mail zur Aufgabe kommt fertig — Nachfassen bzw. Zahlungserinnerung.
  const phase = notiz.offen ? notiz.phase_aufgabe || null : null;
  const phaseMail = !!phase && (phase.regel === "nachhaken" || phase.regel === "zahlungserinnerung")
    && notiz.adressen.length > 0;
  // „Zahlungseingang prüfen" (11.10.2026): ein Klick ordnet zu — Termin und Angebot passen hier nicht.
  const zahlungPruefen = phase?.regel === "zahlung_pruefen";
  // „Helferrechnung prüfen und überweisen" (12.10.2026): öffnet den Beleg — von dort in den
  // Ausgangskorb; eine eingereichte Rechnung kommt erst nach dem Prüfen hinein.
  const helferRechnung = phase?.regel === "helferrechnung";
  const ohneKundentaten = zahlungPruefen || helferRechnung;
  const offenePunkte = notiz.punkte.filter((p) => !p.erledigt_am);
  const stichworte = [...offenePunkte.map((p) => p.text), notiz.text].filter(Boolean).join("\n").slice(0, 1_500);
  const nummer = hauptnummer(k);
  const titel = kompakt ? "" : bezugTitel(notiz, gruppe);
  // Der Kundenname vorn im Titel ist der Weg zu den Kundendaten (13.10.2026).
  const kundeName = k.kunde?.name || "";
  const kundeVorn = !!(b.kunde_id && kundeName && titel.startsWith(kundeName));
  const titelRest = kundeVorn ? titel.slice(kundeName.length).replace(/^ · /, "") : titel;
  const termin = k.termin ? `${k.termin.besichtigung ? "Besichtigung" : "Termin"} ${datumDe(k.termin.start_at)}` : "";
  const bezugQuery = `${b.kunde_id ? `&kunde=${b.kunde_id}` : ""}${b.projekt_id ? `&projekt=${b.projekt_id}` : ""}`;
  // Die Notiz reist mit: legt der Editor ein neues Projekt an, hängen Notiz und Besichtigung danach daran.
  const belegLink = (kind: "quote" | "invoice") =>
    `/rechnungen/neu?kind=${kind}${bezugQuery}&notiz=${notiz.id}&titel=${encodeURIComponent(projektTitelVorschlag(notiz))}`;
  const terminLink = `/planung?planen=1${bezugQuery}&notiz=${notiz.id}`;
  const hatBezug = !!(b.kunde_id || b.projekt_id);
  const passend = passendeTat(notiz);
  const taten: { tat: Tat; knopf: ReactElement }[] = [];
  const dazu = (tat: Tat, bauen: (klasse: string) => ReactElement) => {
    taten.push({ tat, knopf: bauen("nz-tat" + (tat === passend ? " is-passend" : "")) });
  };
  if (notiz.adressen.length > 0 && !bewertung && !phaseMail) dazu("mail", (c) => <button key="mail" type="button" className={c} onClick={() => setSchreiben(true)}>Mail schreiben</button>);
  if (nummer) dazu("anruf", (c) => <a key="anruf" className={c} href={telLink(nummer)}>Anrufen</a>);
  const planerTermin = hatBezug && phase?.regel !== "planen" && !ohneKundentaten;
  // Der Planer-Termin als Knopf nur, wenn die Aufgabe danach klingt — sonst steht er im Menü.
  if (planerTermin && passend === "termin") dazu("termin", (c) => <Link key="termin" className={c} to={terminLink}>Termin</Link>);
  // „Termin" für die Aufgabe selbst: zurückstellen bis zu einem Tag.
  const terminKnopf = !kompakt && notiz.offen && passend !== "termin";
  if (hatBezug && !ohneKundentaten && !telefon) dazu("angebot", (c) => <Link key="angebot" className={c} to={belegLink("quote")}>Angebot</Link>);
  if (hatBezug && passend === "rechnung" && !telefon) dazu("rechnung", (c) => <Link key="rechnung" className={c} to={belegLink("invoice")}>Rechnung</Link>);
  taten.sort((x, y) => Number(y.tat === passend) - Number(x.tat === passend));

  const textSpeichern = () => {
    aendern.mutate({ id: notiz.id, text: entwurf }, { onSuccess: () => setBearbeiten(false) });
  };
  const punktDazu = () => {
    const t = neuerPunkt.trim();
    if (!t) { setPunktOffen(false); return; }
    punktNeu.mutate({ notizId: notiz.id, text: t }, { onSuccess: () => setNeuerPunkt("") });
  };

  return (
    <article className={"nz-karte" + (kompakt ? " is-kompakt" : "") + (notiz.offen ? "" : " is-fertig") + (notiz.wichtig ? " is-wichtig" : "")
      + (titel || termin || !notiz.zustaendig.ich ? "" : " ohne-kopf") + (punkteBearbeiten ? " is-bearbeiten" : "")}>
      <header className="nz-kopf">
        <div className="nz-kopf-titel">
          {titel && (
            <strong>
              {kundeVorn && (onKunde ? (
                <button type="button" className="nz-kunde-link" title="Kundendaten öffnen" onClick={() => onKunde(b.kunde_id)}>{kundeName}</button>
              ) : <Link className="nz-kunde-link" to={`/kunden?kunde=${b.kunde_id}`} title="Zum Kunden">{kundeName}</Link>)}
              {kundeVorn && titelRest ? " · " : ""}{titelRest}
            </strong>
          )}
          {termin && <span className="nz-meta">{termin}</span>}
        </div>
        {!notiz.zustaendig.ich && (
          <span className="nz-person" title={`Zuständig: ${notiz.zustaendig.name}`}>{vorname(notiz.zustaendig.name)}</span>
        )}
        {/* Flagge „wichtig" (20.10.2026): markierte Aufgaben stehen ganz oben. */}
        <button type="button" className={"nz-flagge" + (notiz.wichtig ? " is-an" : "")}
          aria-pressed={!!notiz.wichtig} disabled={aendern.isPending}
          aria-label={notiz.wichtig ? "Nicht mehr wichtig" : "Als wichtig markieren"}
          title={notiz.wichtig ? "Wichtig — tippen zum Aufheben" : "Als wichtig markieren"}
          onClick={() => aendern.mutate({ id: notiz.id, wichtig: !notiz.wichtig })}>
          <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill={notiz.wichtig ? "currentColor" : "none"}
            stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
            <path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" />
          </svg>
        </button>
        <NotizMenue notiz={notiz} aendern={aendern} loeschen={loeschen}
          onTextAendern={() => { setEntwurf(notiz.text); setBearbeiten(true); }}
          punkteBearbeiten={punkteBearbeiten} onPunkteBearbeiten={() => setPunkteBearbeiten((v) => !v)}
          onKunde={!kompakt && b.kunde_id && !kundeVorn && onKunde ? () => onKunde(b.kunde_id) : undefined}
          kundeLink={!kompakt && b.kunde_id && !kundeVorn ? `/kunden?kunde=${b.kunde_id}` : undefined}
          projektLink={!kompakt && b.projekt_id ? `/projekte/${b.projekt_id}` : undefined}
          terminLink={planerTermin && passend !== "termin" ? terminLink : undefined}
          onZurueckstellen={notiz.offen && !terminKnopf ? () => setSpaeterWahl(true) : undefined}
          onVerzichten={bewertung ? () => verzichten.mutate(bewertung.id) : undefined} />
      </header>
      {notiz.zurueckgestellt_bis && <ZurueckgestelltHinweis notizId={notiz.id} bis={notiz.zurueckgestellt_bis} />}
      {spaeterWahl && <ZurueckstellenWahl notizId={notiz.id} onClose={() => setSpaeterWahl(false)} />}

      {bearbeiten ? (
        <div className="nz-bearbeiten">
          <textarea
            className="nz-feld nz-textfeld"
            value={entwurf}
            rows={4}
            onChange={(e) => setEntwurf(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) textSpeichern(); }}
            aria-label="Notiztext"
            autoFocus
          />
          <div className="nz-zeile">
            <button type="button" className="vg-btn vg-btn-primary" disabled={aendern.isPending} onClick={textSpeichern}>Speichern</button>
            <button type="button" className="vg-btn vg-btn-ghost" onClick={() => { setBearbeiten(false); setEntwurf(notiz.text); }}>Abbrechen</button>
          </div>
        </div>
      ) : notiz.text ? (
        <p className="nz-text nz-editierbar" title="Antippen zum Bearbeiten"
          onClick={(e) => { if (aufText(e)) { setEntwurf(notiz.text); setBearbeiten(true); } }}>
          <MitBelegVerweisen text={notiz.text} />
        </p>
      ) : null}

      <NotizPunkte notiz={notiz} aendern={punktAendern} weg={punktWeg} aufText={aufText} />

      {punktOffen && (
        <input
          className="nz-feld"
          value={neuerPunkt}
          maxLength={300}
          autoFocus
          onChange={(e) => setNeuerPunkt(e.target.value)}
          onBlur={() => { if (!neuerPunkt.trim()) setPunktOffen(false); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); punktDazu(); }
            if (e.key === "Escape") { setNeuerPunkt(""); setPunktOffen(false); }
          }}
          placeholder="Punkt, Enter"
          aria-label="Neuer Punkt"
        />
      )}

      {bewertung && notiz.adressen.length === 0 && (
        <p className="bw-ohne-mail" role="note">
          Keine Mailadresse beim Kunden hinterlegt.
          {b.kunde_id && (onKunde ? (
            <button type="button" className="nz-still" onClick={() => onKunde(b.kunde_id)}>Kundendaten öffnen</button>
          ) : <Link className="nz-still" to={`/kunden?kunde=${b.kunde_id}`}>Zum Kunden</Link>)}
        </p>
      )}

      <footer className="nz-fuss">
        {!punktOffen && <button type="button" className="nz-still" onClick={() => setPunktOffen(true)}>+ Punkt</button>}
        {bewertung || phase || terminKnopf || (!kompakt && taten.length > 0) ? (
          <span className="nz-taten">
            {phaseMail && (
              <button type="button" className="nz-tat is-passend" onClick={() => setPhasenMail(true)}>
                {phase?.regel === "zahlungserinnerung" ? "Erinnerung schreiben" : "Nachfassen"}
              </button>
            )}
            {phase?.regel === "planen" && <Link className="nz-tat is-passend" to={terminLink}>Im Planer planen</Link>}
            {helferRechnung && (
              <Link className="nz-tat is-passend" to={`/finanzen?reiter=belege&beleg=${encodeURIComponent(phase.nummer)}`}>
                Rechnung prüfen
              </Link>
            )}
            {zahlungPruefen && (
              <button type="button" className="nz-tat is-passend" disabled={zuordnen.isPending}
                onClick={() => zuordnen.mutate(notiz.id)}>
                {zuordnen.isPending ? "Ordne zu …" : "Zahlung zuordnen"}
              </button>
            )}
            {bewertung && notiz.adressen.length > 0 && (
              <button type="button" className="nz-tat is-passend" onClick={() => setBewerten(true)}>Bewertung erbitten</button>
            )}
            {!kompakt && taten.map((t) => t.knopf)}
            {terminKnopf && (
              <button type="button" className={"nz-tat" + (spaeterWahl ? " is-aktiv" : "")} aria-expanded={spaeterWahl}
                title="Der Aufgabe einen Termin geben — bis dahin ist sie zurückgestellt"
                onClick={() => setSpaeterWahl((v) => !v)}>Termin</button>
            )}
            {/* Automatische Aufgaben passen nicht immer (11.10.2026): weg, ohne sie als erledigt abzuhaken. */}
            {bewertung && (
              <button type="button" className="nz-still" disabled={verzichten.isPending}
                onClick={() => verzichten.mutate(bewertung.id)}>Nicht fragen</button>
            )}
            {phase && (
              <button type="button" className="nz-still" disabled={loeschen.isPending}
                onClick={() => loeschen.mutate(notiz.id)}>Entfällt</button>
            )}
          </span>
        ) : null}
      </footer>

      {fehler && <p className="nz-fehler" role="alert">{fehlerText(fehler)}</p>}

      {phasenMail && phaseMail && (
        <PhasenMail notizId={notiz.id} ziele={notiz.adressen} onClose={() => setPhasenMail(false)} />
      )}

      {bewerten && bewertung && (
        <BewertungSchreiben bewertungId={bewertung.id} ziele={notiz.adressen} onClose={() => setBewerten(false)} />
      )}

      {schreiben && (
        <PostSchreiben
          onClose={() => setSchreiben(false)}
          projektId={b.projekt_id || undefined}
          kundeId={b.kunde_id || undefined}
          anfrageId={b.anfrage_id || undefined}
          lieferantId={b.lieferant_firma_id || undefined}
          ziele={notiz.adressen}
          an={notiz.adressen[0]?.email || ""}
          stichworte={stichworte}
          titel={bezugTitel(notiz)}
        />
      )}
    </article>
  );
}
